import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_LOCAL_URL } from '../../../../config';

const emptyProcess = {
  processCode: "",
  processDescription: "",
  criticalSteps: "",
  physical: "",
  chemical: "",
  biological: "",
  killStepReview: "",
  rawMaterial: "",
  processControls: "",
  organicControls: "",
  qualityControl: "",
  thirdPartySampling: "",

  // STORE ONLY S3 KEYS FOR DATABASE
  processFlowChartUrl: "",
  studyUrl: "",
  msdsUrl: "",

  // FRONTEND PREVIEW URLS (SIGNED URLS)
  processFlowChartPreview: "",
  studyPreview: "",
  msdsPreview: "",
  processFlowChartFile: null,
  studyFile: null,
  msdsFile: null,  

  acceleratedStability: "",
  realTimeStability: "",
  oxidativeStability: "",
  photoStability: "",
  radiationStudy: "",
  shelfLife: "",
  handling: "",
  capsule: "",
  tablet: "",
  foodPreparations: "",
  cosmeticPreparations: "",
  mixability: "",
  studyType: "",
  significance: "",
  validation: "",
  references: "",
};

const processFields = [
  { key: "processCode", label: "Process Code", required: true },
  { key: "processDescription", label: "Process Description", required: true },
  { key: "criticalSteps", label: "Critical Steps" },
  { key: "physical", label: "Physical" },
  { key: "chemical", label: "Chemical" },
  { key: "biological", label: "Biological" },
  { key: "killStepReview", label: "Kill Step Review" },
];

const validationFields = [
  { key: "rawMaterial", label: "Raw Material" },
  { key: "processControls", label: "Process Controls" },
  { key: "organicControls", label: "Organic Controls" },
  { key: "qualityControl", label: "Quality Control" },
  { key: "thirdPartySampling", label: "Third Party Sampling" },
];

export default function ProcessSpecificationForm({
  data,
  onChange,
  onNext,
  onBack,
  errors = {},
}) {
  const items = data.processes || [];
  const [existingProcesses, setExistingProcesses] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");

  const setItems = (arr, meta) => onChange("processes", arr, meta);

  const handleChange = (index, key, value) => {
    const arr = [...items];
    arr[index] = {
      ...arr[index],
      [key]: value,
    };
    setItems(arr, { clearAt: { index, key } });
  };

  const addItem = () => setItems([...items, { ...emptyProcess }]);

  const removeItem = (index) => {
    const arr = [...items];
    arr.splice(index, 1);
    setItems(arr);
  };

  const addExistingProcess = () => {
    if (!selectedCode) return;
    const selected = existingProcesses.find((proc) => proc.processCode === selectedCode);

    if (selected) {
      setItems([
        ...items,
        {
          ...emptyProcess,
          ...selected,
          // If the backend sent a full URL in the Url field, we move it to preview
          // and keep the key logic clean.
           processFlowChartPreview:
          selected.processFlowChartSignedUrl || "",

        studyPreview:
          selected.studySignedUrl || "",

        msdsPreview:
          selected.msdsSignedUrl || "",
              },
      ]);
      setSelectedCode("");
    }
  };


  useEffect(() => {
    axios
      .get(`${API_BASE_LOCAL_URL}/api/processes`)
      .then((res) => setExistingProcesses(res.data))
      .catch((err) => console.error("Failed to fetch processes", err));
  }, []);

  return (
    <div>
      <h4 className="text-success">Process Specification</h4>

      <div className="mt-3 mb-3">
        <label className="form-label">Select Existing Process</label>
        <div className="d-flex">
          <select
            className="form-select me-2"
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
          >
            <option value="">-- Choose Process --</option>
            {existingProcesses.map((proc) => (
              <option key={proc.processCode} value={proc.processCode}>
                {proc.processDescription} ({proc.processCode})
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-success" onClick={addExistingProcess}>
            Add
          </button>
        </div>
      </div>

      {items.map((item, i) => {
        const rowErrors = errors[i] || {};

        return (
          <div key={i} className="border rounded p-3 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Process #{i + 1}</strong>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(i)}>
                Remove
              </button>
            </div>

            <div className="row">
              {processFields.map(({ key, label, required }) => (
                <div className="col-md-4 mb-2" key={key}>
                  <input
                    className={`form-control ${rowErrors[key] ? "is-invalid" : ""}`}
                    placeholder={label + (required ? " *" : "")}
                    value={item[key] ?? ""}
                    onChange={(e) => handleChange(i, key, e.target.value)}
                  />
                  {rowErrors[key] && <div className="invalid-feedback">{rowErrors[key]}</div>}
                </div>
              ))}
            </div>

            <h6 className="mt-3">Validations</h6>
            <div className="row">
              {validationFields.map(({ key, label }) => (
                <div className="col-md-4 mb-2" key={key}>
                  <input
                    className="form-control"
                    placeholder={label}
                    value={item[key] ?? ""}
                    onChange={(e) => handleChange(i, key, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <h6 className="mt-3">Reference Documents Upload</h6>
            <div className="row">
              {/* FLOW CHART */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Process Flow Chart</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => {

  const file = e.target.files[0];

  if (!file) return;

  const updatedItems = [...items];

  updatedItems[i] = {
    ...updatedItems[i],
    processFlowChartFile: file,
    processFlowChartPreview: URL.createObjectURL(file),
  };

  setItems(updatedItems);

}}
                />
                {(item.processFlowChartPreview || item.processFlowChartUrl) && (
                  <a href={item.processFlowChartPreview || item.processFlowChartUrl} target="_blank" rel="noreferrer" className="small d-block mt-1">
                    View Existing File
                  </a>
                )}
              </div>

              {/* STUDY FILE */}
              <div className="col-md-4 mb-3">
                <label className="form-label">Study File</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => {

  const file = e.target.files[0];

  if (!file) return;

  const updatedItems = [...items];

  updatedItems[i] = {
    ...updatedItems[i],
    studyFile: file,
    studyPreview: URL.createObjectURL(file),
  };

  setItems(updatedItems);

}}
                />
                {(item.studyPreview || item.studyUrl) && (
                  <a href={item.studyPreview || item.studyUrl} target="_blank" rel="noreferrer" className="small d-block mt-1">
                    View Existing File
                  </a>
                )}
              </div>

              {/* MSDS FILE */}
              <div className="col-md-4 mb-3">
                <label className="form-label">MSDS File</label>
                <input
                  type="file"
                  className="form-control"
                  accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => {

  const file = e.target.files[0];

  if (!file) return;

  const updatedItems = [...items];

  updatedItems[i] = {
    ...updatedItems[i],
    msdsFile: file,
    msdsPreview: URL.createObjectURL(file),
  };

  setItems(updatedItems);

}}
                />
                {(item.msdsPreview || item.msdsUrl) && (
                  <a href={item.msdsPreview || item.msdsUrl} target="_blank" rel="noreferrer" className="small d-block mt-1">
                    View Existing File
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <button type="button" className="btn btn-light" onClick={addItem}>
        Add New Process
      </button>

      <div className="d-flex justify-content-between mt-3">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn btn-primary" onClick={onNext}>
          Next
        </button>
      </div>
    </div>
  );
}